import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy, faEye, faThumbsUp, faComment, faGaugeHigh } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";

const MEDAL_COLORS = ["#c9960a", "#9299a8", "#a0522d"];
const MEDAL_BG    = ["rgba(201,150,10,0.12)", "rgba(146,153,168,0.12)", "rgba(160,82,45,0.12)"];

const TopPosts = ({ onEdit }) => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const response = await crudApi.justGet(SocialEndpoints.posts.top);
      if (response.ok) setPosts(response.data.posts ?? []);
      setLoading(false);
    };
    fetch();
  }, []);

  return (
    <div style={{ background: "#fff", border: "1px solid #eaecf0", borderRadius: 16, padding: "20px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(201,150,10,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FontAwesomeIcon icon={faTrophy} style={{ color: "#c9960a", fontSize: 16 }} />
        </div>
        <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#1a2236" }}>
          {t("Backend.Dashboard.TopPosts", "Top Posts")}
        </h6>
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <div className="skeleton" style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="skeleton" style={{ height: 12, width: "70%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 10, width: "45%" }} />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <p style={{ fontSize: 13, color: "#9299a8", margin: 0 }}>
          {t("Backend.Dashboard.NoTopPosts", "No published posts with stats yet.")}
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {posts.map((post, i) => {
            const stat = post.stats?.at(-1);
            const score = Number(stat?.score) || 0;
            return (
              <div
                key={post.id}
                onClick={() => onEdit?.(post)}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px", borderRadius: 12, cursor: "pointer",
                  background: "#f8fafc", border: "1px solid #eaecf0",
                  transition: "box-shadow 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = MEDAL_COLORS[i] + "60"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#eaecf0"; }}
              >
                {/* Rank badge */}
                <div style={{ width: 28, height: 28, borderRadius: 8, background: MEDAL_BG[i], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <FontAwesomeIcon icon={faTrophy} style={{ color: MEDAL_COLORS[i], fontSize: 13 }} />
                </div>

                {/* Title + stats */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 4 }}>
                    {post.title || "—"}
                  </p>
                  {stat && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, color: "#9299a8", display: "flex", alignItems: "center", gap: 3 }}>
                        <FontAwesomeIcon icon={faEye} style={{ fontSize: 10 }} /> {parseInt(stat.views, 10) || 0}
                      </span>
                      <span style={{ fontSize: 11, color: "#9299a8", display: "flex", alignItems: "center", gap: 3 }}>
                        <FontAwesomeIcon icon={faThumbsUp} style={{ fontSize: 10 }} /> {parseInt(stat.likes, 10) || 0}
                      </span>
                      <span style={{ fontSize: 11, color: "#9299a8", display: "flex", alignItems: "center", gap: 3 }}>
                        <FontAwesomeIcon icon={faComment} style={{ fontSize: 10 }} /> {parseInt(stat.comments, 10) || 0}
                      </span>
                    </div>
                  )}
                </div>

                {/* Score */}
                {score > 0 && (
                  <span style={{ fontSize: 13, fontWeight: 700, color: MEDAL_COLORS[i], display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <FontAwesomeIcon icon={faGaugeHigh} style={{ fontSize: 12 }} />
                    {Math.round(score)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TopPosts;
